import type { ReactNode } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import { ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES } from '../feature-flags';
import { MOTION_EDITOR_WORLD_OBJECT_HOTKEYS } from '../worldObjects';

interface MotionEditorHotkeyInfoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface HotkeySection {
  name: string;
  enabled?: boolean;
  hotkeys: Hotkey[];
}

interface Hotkey {
  name: string;
  keys: string[];
}

const HOTKEY_SECTIONS: HotkeySection[] = [
  {
    name: 'Player',
    hotkeys: [
      { name: 'Play/Pause', keys: ['Space'] },
      { name: 'Jump frame', keys: ['Left / Right'] },
      { name: 'Jump 10 frames', keys: ['Shift + Left / Right'] },
    ],
  },
  {
    name: 'Camera',
    hotkeys: [
      { name: 'Hold to pan', keys: ['Shift'] },
      { name: 'Focus character', keys: ['C'] },
      { name: 'Focus selected joint', keys: ['F'] },
    ],
  },
  {
    name: 'Key poses',
    enabled: ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES,
    hotkeys: [
      { name: 'Add key pose', keys: ['S'] },
      { name: 'Remove key pose', keys: ['Shift + S'] },
      { name: 'Hide/show key pose', keys: ['H'] },
      { name: 'Previous/next key pose', keys: [', / .'] },
      { name: 'Switch to Move tool', keys: ['W'] },
      { name: 'Switch to Rotate tool', keys: ['E'] },
      { name: 'Clear selection', keys: ['Q', 'Escape'] },
      { name: 'Undo edit', keys: ['Cmd + Z', 'Ctrl + Z'] },
      {
        name: 'Redo edit',
        keys: ['Cmd + Shift + Z', 'Ctrl + Shift + Z'],
      },
    ],
  },
  {
    name: 'World objects',
    hotkeys: MOTION_EDITOR_WORLD_OBJECT_HOTKEYS,
  },
];

export default function MotionEditorHotkeyInfo({
  open,
  onOpenChange,
}: MotionEditorHotkeyInfoProps) {
  useHotkeys(
    'shift+h',
    () => {
      onOpenChange(!open);
    },
    {
      preventDefault: true,
    },
    [onOpenChange, open],
  );

  useHotkeys(
    'escape',
    () => {
      onOpenChange(false);
    },
    {
      enabled: open,
      preventDefault: true,
    },
    [onOpenChange, open],
  );

  if (!open) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-stretch justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close hotkeys"
        className="pointer-events-auto absolute inset-0 z-0 cursor-default"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="motion-editor-hotkeys-title"
        className="bg-gray-90/90 pointer-events-auto z-10 flex max-h-full w-full flex-col overflow-y-auto p-4 text-white sm:max-h-[90vh] sm:w-auto sm:max-w-[560px] sm:min-w-[440px] sm:rounded-2xl sm:p-5"
      >
        <div className="border-gray-60 mb-4 flex flex-col border-b sm:mb-0 sm:border-b-0">
          <Text
            id="motion-editor-hotkeys-title"
            as="h2"
            token="20/medium"
            className="mb-4 text-white"
          >
            Motion Editor hotkeys
          </Text>
          {HOTKEY_SECTIONS.map((section) =>
            section.enabled === false ? null : (
              <HotkeyScope key={section.name} name={section.name}>
                <Hotkeys hotkeys={section.hotkeys} />
              </HotkeyScope>
            ),
          )}
        </div>
        <Button
          onClick={() => onOpenChange(false)}
          className="mt-auto sm:hidden"
        >
          Close
        </Button>
      </div>
    </div>
  );
}

interface HotkeyScopeProps {
  children: ReactNode;
  name: string;
}

function HotkeyScope({ children, name }: HotkeyScopeProps) {
  return (
    <div className="border-gray-60 relative mb-4 flex flex-col gap-3 border-b pb-4 last:border-b-0">
      <Text as="h3" token="14/medium" className="text-white">
        {name}
      </Text>
      {children}
    </div>
  );
}

interface HotkeysProps {
  hotkeys: Hotkey[];
}

function Hotkeys({ hotkeys }: HotkeysProps) {
  return (
    <dl className="grid grid-cols-[minmax(120px,auto)_1fr] items-center justify-items-start gap-x-4 gap-y-4">
      {hotkeys.map((hotkey) => (
        <HotkeyDefinition key={hotkey.name} hotkey={hotkey} />
      ))}
    </dl>
  );
}

interface HotkeyDefinitionProps {
  hotkey: Hotkey;
}

function HotkeyDefinition({ hotkey }: HotkeyDefinitionProps) {
  return (
    <>
      <Text as="dt" token="14" className="text-gray-45">
        {hotkey.name}
      </Text>
      <Text as="dd" token="14" className="flex flex-wrap gap-1.5 text-white">
        {hotkey.keys.map((key, index) => (
          <HotkeySequence
            key={`${hotkey.name}-${key}`}
            sequence={key}
            trailingComma={index < hotkey.keys.length - 1}
          />
        ))}
      </Text>
    </>
  );
}

interface HotkeySequenceProps {
  sequence: string;
  trailingComma: boolean;
}

function HotkeySequence({ sequence, trailingComma }: HotkeySequenceProps) {
  const chordParts = sequence.split(' + ');

  return (
    <span className="inline-flex items-center gap-1.5">
      {chordParts.map((part, partIndex) => (
        <span
          key={`${sequence}-${part}-${partIndex}`}
          className="inline-flex items-center gap-1.5"
        >
          {part.split(' / ').map((key, keyIndex, keys) => (
            <span
              key={`${sequence}-${part}-${key}-${keyIndex}`}
              className="inline-flex items-center gap-1.5"
            >
              <kbd className="border-gray-60 bg-gray-75 rounded-md border px-1.5 py-0.5 font-mono text-white shadow-sm">
                {key}
              </kbd>
              {keyIndex < keys.length - 1 ? <span>/</span> : null}
            </span>
          ))}
          {partIndex < chordParts.length - 1 ? <span>+</span> : null}
        </span>
      ))}
      {trailingComma ? <span>,</span> : null}
    </span>
  );
}
